// controls/TextAreaField/TextAreaField.tsx
import * as React from "react";
import { FieldLabelRight } from "@/ui/form/primitives";
import {
    FieldLabel,
    FieldHelper,
    FieldErrorMessage,
} from "@/ui/form/primitives";
import { useStableId } from "../hooks/useStableId";

type Props = React.ComponentProps<'textarea'> & {
    label?: React.ReactNode;
    helper?: React.ReactNode;
    labelRight?: React.ReactNode;
    error?: string | null;
    fullWidth?: boolean;
    maxLength?: number;
};

export function TextAreaField({ label, labelRight, helper, error, id, fullWidth, maxLength, value, ...props }: Props) {
    const autoId = useStableId()
    const inputId = id ?? `autoid-${autoId}`;
    const helperId = helper ? `${inputId}-helper` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;

    const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined;

    const currentLength = React.useMemo(() => {
        if (value === undefined || value === null) return 0;
        return String(value).length;
    }, [value]);

    return (
        <div data-control="text-area-field" className={`tw-flex tw-flex-col tw-gap-1 ${fullWidth ? 'tw-w-full' : ''}`}>
            {(label || labelRight) && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                    {label && (
                        <FieldLabel htmlFor={inputId}>
                            {label}
                            {props.required && <span className="tw-text-red-500 tw-ml-0.5">*</span>}
                        </FieldLabel>
                    )}
                    {labelRight && (
                        <FieldLabelRight className="tw-text-sm tw-text-gray-500">
                            {labelRight}
                        </FieldLabelRight>
                    )}
                </div>
            )}

            <textarea
                id={inputId}
                aria-describedby={describedBy}
                aria-invalid={!!error || undefined}
                value={value}
                maxLength={maxLength}
                className={`
          tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300
          tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-leading-tight
          tw-placeholder-gray-400 tw-shadow-sm tw-resize-y
          focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500
          disabled:tw-bg-gray-100 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed
          data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500
        `}
                {...props}
            />

            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                {helper && (
                    <FieldHelper
                        id={helperId}
                        className="tw-text-xs tw-text-gray-500 tw-mt-0.5"
                    >
                        {helper}
                    </FieldHelper>
                )}

                {maxLength !== undefined && (
                    <div className="tw-text-xs  tw-w-full tw-flex tw-justify-end tw-text-content-base tw-mt-0.5 tw-shrink-0">
                        {currentLength} / {maxLength}
                    </div>
                )}
            </div>

            {error && (
                <FieldErrorMessage
                    id={errorId}
                    className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-0.5"
                >
                    {error}
                </FieldErrorMessage>
            )}
        </div>
    );
}
